import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Bot, Globe, Layers, Smartphone, Users, Cpu } from "lucide-react";

const capabilities = [
  { icon: Bot, label: "Custom AI Agents", desc: "Agents built for your exact workflows" },
  { icon: Users, label: "Internal Enterprise AI Tools", desc: "Boost team productivity with AI" },
  { icon: Layers, label: "Workflow Automation", desc: "End-to-end process automation" },
  { icon: Smartphone, label: "Mobile Applications", desc: "AI-native iOS & Android apps" },
  { icon: Globe, label: "SaaS Platforms", desc: "Full-stack AI-powered products" },
  { icon: Cpu, label: "Multi-Agent Systems", desc: "Coordinated autonomous AI networks" },
];

const CustomSolutions = () => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="solutions"
      data-testid="custom-solutions-section"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#050505] relative overflow-hidden"
    >
      {/* Background accent */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 80% 50%, rgba(0, 229, 153, 0.05) 0%, transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div
            className={`transition-all duration-700 ${
              inView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <span className="text-xs font-bold text-[#00E599] uppercase tracking-widest block mb-4">
              Enterprise AI Development
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold text-white mb-5"
              style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.025em" }}
              data-testid="custom-solutions-heading"
            >
              Need Something Unique?
            </h2>
            <p className="text-[#A1A1AA] text-lg leading-relaxed mb-8">
              We design and develop custom AI systems tailored specifically to your business
              workflows, customers, and operations. From concept to production — we own it all.
            </p>

            <div className="flex flex-col gap-4 mb-10">
              {[
                "Deep understanding of your business model & goals",
                "Custom architecture designed from the ground up",
                "Full-stack delivery: backend, frontend, AI layer",
                "Ongoing support, monitoring, and iteration",
              ].map((point, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#00E599]/15 border border-[#00E599]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#00E599]" />
                  </div>
                  <span className="text-sm text-[#A1A1AA]">{point}</span>
                </div>
              ))}
            </div>

            <a
              href="#contact"
              data-testid="custom-solutions-cta"
              className="inline-flex items-center gap-2 bg-[#00E599] text-black font-semibold rounded-full px-8 py-4 hover:bg-[#00ff99] transition-all duration-200 hover:shadow-[0_0_25px_rgba(0,229,153,0.35)]"
            >
              Discuss Your Project
              <ArrowRight size={16} />
            </a>
          </div>

          {/* Right: Capability Grid */}
          <div
            className={`transition-all duration-700 delay-200 ${
              inView ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {capabilities.map((cap, index) => {
                const Icon = cap.icon;
                return (
                  <div
                    key={index}
                    data-testid={`capability-card-${index}`}
                    className="bg-[#0A0A0A] border border-white/6 rounded-2xl p-6 hover:border-[#00E599]/20 transition-all duration-300 group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#00E599]/8 border border-[#00E599]/12 flex items-center justify-center mb-4 group-hover:bg-[#00E599]/15 transition-colors">
                      <Icon size={18} className="text-[#00E599]" />
                    </div>
                    <h4
                      className="font-semibold text-white text-sm mb-1"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {cap.label}
                    </h4>
                    <p className="text-xs text-[#71717A]">{cap.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* Enterprise badge */}
            <div className="mt-5 p-5 rounded-2xl bg-[#00E599]/4 border border-[#00E599]/12">
              <p className="text-sm text-[#A1A1AA]">
                <span className="text-[#00E599] font-semibold">Enterprise-Grade Quality — </span>
                Built with production-ready architecture, security best practices, and
                scalable infrastructure from day one.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomSolutions;
